import React, { useState, useEffect } from 'react'
import { signUpStyles } from './style'
import { Box, Checkbox, TextField, Grid, MenuItem, FormControlLabel, Button } from '@material-ui/core'
import { useFormik } from 'formik'
import { connect } from 'react-redux'
import { clearMessage, userSignUp } from '../../AppRedux/actions/Auth'
import * as yup from 'yup'
import _ from 'lodash'
import Icon from '../../utils/icons'
import DisplayMessage from '../../Components/DisplayMessage'
import { COMMON } from '../../utils/constants'
import { utilService } from '../../Services/UtilService/UtilService'
import { Loader, AppButton } from '../../Components'
import * as messageTypes from '../../utils/messageTypes'
import { pushToDataLayer, USERBEHAVIOURTRACKING } from '../../utils/gaEnvironment'

const ALL = 'ALL'
let validated = false
export const debounceValidateWebsite = _.debounce(async (value, resolve) => {
  await utilService.validateWebsite(value)
    .then(validatedData => {
      validated = true
      resolve(true)
    })
    .catch(error => {
      validated = true
      resolve(false)
    })
}, 1000)
const validationSchema = yup.object({
  company_name: yup
    .string('Enter your account name')
    .max(50, 'Account name must be max 50 characters')
    .required('Account name is required'),
  registered_as: yup
    .string('Select Register as')
    .required('Select Register as'),
  email: yup
    .string('Enter your email')
    .email('Enter a valid email')
    .required('Email is required'),
  phone: yup
    .string('Enter phone number')
    .matches(/(^[0-9]{10})$/, 'Enter valid phone number')
    .required('Phone number is required'),
  name: yup
    .string('Enter full name')
    .required('Full name is required'),
  website: yup
    .string('Enter your website')
    .test("website", "Enter valid website", async (value) => {
      return new Promise((resolve) => debounceValidateWebsite(value, resolve))
    })
    .required('Website is required'),
  address: yup
    .string('Enter your address')
    .max(100, 'Address must be max 100 characters')
    .required('Address is required'),
  city: yup
    .string('Enter your city')
    .max(100, 'City must be max 100 characters')
    .required('City is required'),
  state: yup
    .string('Select your state')
    .required('Select your state'),
  country: yup
    .string('Select your country')
    .required('Select your country'),
  timezone: yup
    .string('Select the timezone')
    .required('Timezone is required')
})

function SignUp(props) {
  const [open, setOpen] = useState(false)
  const [openMessage, setOpenMessage] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('')
  const [checked, setChecked] = useState(false)
  const [countries, setCountries] = useState('')
  const [states, setStates] = useState({})
  const [timezones, setTimezones] = useState({})
  const [loading, setLoading] = useState(true)
  const classes = signUpStyles()
  const formik = useFormik({
    initialValues: {
      company_name: '',
      registered_as: '',
      email: '',
      phone: '',
      name: '',
      website: '',
      address: '',
      city: '',
      state: '',
      country: '',
      timezone: '',
      accept: '',
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      props.userSignUp(values)
      pushToDataLayer({ event: USERBEHAVIOURTRACKING })
    },
  })

  const getAllCountries = () => {
    utilService.getAllCountries()
      .then(countriesData => {
        setCountries(countriesData)
        setLoading(false)
      })
      .catch(error => {
        setLoading(false)
        setOpenMessage(true)
        setMessageType(messageTypes.INFO)
        setMessage(_.get(error, 'data.error.displayMessage', 'Failed to fetch countries'))
      })
  }

  useEffect(() => {
    if (props.message) {
      setOpen(true)
      const clear = setTimeout(() => {
        props.clearMessage()
      }, 6000)
      return () => clearTimeout(clear)
    }
  }, [props.message])

  useEffect(() => {
    getAllCountries()
  }, [])

  useEffect(() => {
    if (!open) {
      props.clearMessage()
    }
  }, [open])

  const handleClose = (event, reason) => {
    if (reason === COMMON.CLICKAWAY) {
      return
    }
    setOpen(false)
    setOpenMessage(false)
  }

  const getAllStates = (country) => {
    setLoading(true)
    utilService.getStateByCountry(country)
      .then(stateData => {
        if (_.isEmpty(stateData)) {
          setStates({ 'ALL': 'ALL' })
          formik.setFieldValue('state', 'ALL')
        } else {
          setStates(stateData)
        }
        setLoading(false)
      })
      .catch(error => {
        setLoading(false)
        setOpenMessage(true)
        setMessageType(messageTypes.INFO)
        setMessage(_.get(error, 'data.error.displayMessage', 'Failed to fetch states'))
      })
  }

  const getTimezones = (country) => {
    setLoading(true)
    utilService.getTimezonesByCountry(country)
      .then(timeZoneData => {
        setTimezones(timeZoneData)
        setLoading(false)
      })
      .catch(error => {
        setLoading(false)
        setOpenMessage(true)
        setMessageType(messageTypes.INFO)
        setMessage(_.get(error, 'data.error.displayMessage', 'Failed to fetch timezones'))
      })
  }

  const handleCountryChange = (e) => {
    formik.setFieldValue([e.target.name], e.target.value)
    formik.setFieldValue('state', '')
    formik.setFieldValue('timezone', '')
    getAllStates(e.target.value)
    getTimezones(e.target.value)
  }

  const handleCheckbox = (e) => {
    setChecked(e.target.checked)
  }

  const renderIcon = (errors, touched) => {
    return validated ? touched ? _.isUndefined(errors) ? <Icon src="circleTick" /> : <Icon src="circleCross" /> : <></> : <></>
  }

  return <>
    <div className={classes.SignUpDiv}>
      {props.message && props.messageType && <DisplayMessage open={open} message={props.message} messageType={props.messageType} handleClose={handleClose} />}
      <DisplayMessage open={openMessage} message={message} messageType={messageType} handleClose={handleClose} />
      <Loader loading={loading || props.loading} />
      <Box className={classes.SignUpBox}>
        <Box className={classes.SignUpTextBox}>
          <p>Sign Up</p>
        </Box>
        <hr />
        <Box className={classes.container}>
          <div className={classes.headerWrapper}>Personal Details</div>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={4} className={classes.innerContainer}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="full-name"
                  name="name"
                  label="Full Name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={(e) => formik.setFieldValue('name', e.target.value.trim())}
                  InputProps={{
                    endAdornment: renderIcon(formik.errors.name, formik.touched.name),
                  }}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="phone"
                  name="phone"
                  label="Phone"
                  value={formik.values.phone}
                  onChange={(e) => formik.setFieldValue('phone', e.target.value.trim())}
                  InputProps={{
                    endAdornment: renderIcon(formik.errors.phone, formik.touched.phone),
                  }}
                  error={formik.touched.phone && Boolean(formik.errors.phone)}
                  helperText={formik.touched.phone && formik.errors.phone}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  label="Email"
                  value={formik.values.email}
                  onChange={(e) => formik.setFieldValue('email', e.target.value.trim())}
                  InputProps={{
                    endAdornment: renderIcon(formik.errors.email, formik.touched.email),
                  }}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </Grid>
              <Grid item xs={12} sm={6}></Grid>
            </Grid>
            <div className={classes.headerWrapper}>Account Details</div>
            <Grid container spacing={4} className={classes.innerContainer}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="companyname"
                  name="company_name"
                  label="Account Name"
                  value={formik.values.company_name}
                  onChange={formik.handleChange}
                  onBlur={(e) => formik.setFieldValue('company_name', e.target.value.trim())}
                  InputProps={{
                    endAdornment: renderIcon(formik.errors.company_name, formik.touched.company_name),
                  }}
                  error={formik.touched.company_name && Boolean(formik.errors.company_name)}
                  helperText={formik.touched.company_name && formik.errors.company_name}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  style={{ textAlign: 'left' }}
                  select
                  id="register-as"
                  name="registered_as"
                  label="Register As"
                  value={formik.values.registered_as}
                  onChange={formik.handleChange}
                  InputProps={{
                    classes: { root: classes.input }
                  }}
                  SelectProps={{
                    MenuProps: {
                      classes: { paper: classes.paper },
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left"
                      },
                      getContentAnchorEl: null
                    }
                  }}
                  error={formik.touched.registered_as && Boolean(formik.errors.registered_as)}
                  helperText={formik.touched.registered_as && formik.errors.registered_as}
                >
                  <MenuItem value='aggregator'>Aggregator</MenuItem>
                  <MenuItem value='utility'>Utility</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="address"
                  name="address"
                  label="Address"
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  onBlur={(e) => formik.setFieldValue('address', e.target.value.trim())}
                  InputProps={{
                    endAdornment: renderIcon(formik.errors.address, formik.touched.address),
                  }}
                  error={formik.touched.address && Boolean(formik.errors.address)}
                  helperText={formik.touched.address && formik.errors.address}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="city"
                  name="city"
                  label="City"
                  value={formik.values.city}
                  onChange={formik.handleChange}
                  onBlur={(e) => formik.setFieldValue('city', e.target.value.trim())}
                  InputProps={{
                    endAdornment: renderIcon(formik.errors.city, formik.touched.city),
                  }}
                  error={formik.touched.city && Boolean(formik.errors.city)}
                  helperText={formik.touched.city && formik.errors.city}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  style={{ textAlign: 'left' }}
                  id="country"
                  name="country"
                  label="Country"
                  value={formik.values.country}
                  onChange={handleCountryChange}
                  InputProps={{
                    classes: { root: classes.input }
                  }}
                  SelectProps={{
                    MenuProps: {
                      classes: { paper: classes.paper },
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left"
                      },
                      getContentAnchorEl: null
                    }
                  }}
                  error={formik.touched.country && Boolean(formik.errors.country)}
                  helperText={formik.touched.country && formik.errors.country}
                >
                  {Object.keys(countries).map(key => (
                    <MenuItem key={key} value={key}>{countries[key]}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  style={{ textAlign: 'left' }}
                  id="state"
                  name="state"
                  label="State"
                  disabled={!formik.values.country}
                  value={formik.values.state}
                  onChange={formik.handleChange}
                  InputProps={{
                    classes: { root: classes.input }
                  }}
                  SelectProps={{
                    MenuProps: {
                      classes: { paper: classes.paper },
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left"
                      },
                      getContentAnchorEl: null
                    }
                  }}
                  error={formik.touched.state && Boolean(formik.errors.state)}
                  helperText={formik.touched.state && formik.errors.state}
                >
                  {Object.keys(states).map((state) => (
                    <MenuItem key={state} value={state}>{`${states[state]}${state !== ALL ? ' - ' + state : ''}`}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="website"
                  name="website"
                  label="Website"
                  value={formik.values.website}
                  onChange={(e) => formik.setFieldValue('website', e.target.value.trim())}
                  InputProps={{
                    endAdornment: renderIcon(formik.errors.website, formik.touched.website),
                  }}
                  error={formik.touched.website && Boolean(formik.errors.website)}
                  helperText={formik.touched.website && formik.errors.website}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  style={{ textAlign: 'left' }}
                  id="timezone"
                  name="timezone"
                  label="Time Zone"
                  disabled={!formik.values.country}
                  value={formik.values.timezone}
                  onChange={formik.handleChange}
                  InputProps={{
                    classes: { root: classes.input }
                  }}
                  SelectProps={{
                    MenuProps: {
                      classes: { paper: classes.paper },
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left"
                      },
                      getContentAnchorEl: null
                    }
                  }}
                  error={formik.touched.timezone && Boolean(formik.errors.timezone)}
                  helperText={formik.touched.timezone && formik.errors.timezone}
                >
                  {Object.keys(timezones).map((timezone) => (
                    <MenuItem key={timezone} value={timezone}>{timezone}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <Box className={`${classes.flexSpaceBtw} ${classes.innerContainer}`}>
              <FormControlLabel
                control={
                  <Checkbox
                    id="accept"
                    name="accept"
                    checked={checked}
                    onChange={handleCheckbox}
                    color="primary"
                  />
                }
                label={(
                  <span style={{ fontSize: 14 }}>
                    I have read the&nbsp;
                    <a href="https://enphase.com/legal/privacy-policy" target="_blank" className={classes.forgotPassword}>Privacy Policy</a>, and agree to the&nbsp;
                    <a href="https://enphase.com/legal/terms-of-service" target="_blank" className={classes.forgotPassword}>Terms of Services</a>&nbsp;
                  </span>
                )}
              />
            </Box>
            <AppButton disabled={!checked} fill={true} type="submit" text="Sign up" fullWidth />
          </form>
        </Box>
        <hr />
        <Box className={`${classes.container} ${classes.bottomButton}`}>
          <p>Already have an account?</p>
          <AppButton text="Sign In" fill={false} onClick={() => props.history.push('/signin')} />
        </Box>
      </Box>
    </div>
  </>
}

const mapStateToProps = ({ auth }) => {
  const { message, loading, messageType } = auth
  return { message, loading, messageType }
}

const mapDispatchToProps = dispatch => ({
  userSignUp: payload => dispatch(userSignUp(payload)),
  clearMessage: payload => dispatch(clearMessage())
})

export default connect(mapStateToProps, mapDispatchToProps)(SignUp)